import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Tabs,
  Tab,
  Card,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Tooltip as MuiTooltip,
  LinearProgress,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import { fetchPurchaseData } from '../services/odata';
import { buildAllAnalytics } from '../services/purchaseAnalytics';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'po', label: 'Purchase Orders' },
  { key: 'pr', label: 'Requisitions' },
  { key: 'gr', label: 'Goods Receipts' },
  { key: 'ap', label: 'Vendor Invoices' },
];

const PALETTE = ['#4C9AFF', '#57D9A3', '#FFAB4C', '#FF7452', '#B388FF', '#4DD0E1', '#F06292', '#AED581'];

const TONE = { positive: '#57D9A3', negative: '#FF7452', warning: '#FFAB4C', neutral: '#4C9AFF' };

// The app theme sets shape.borderRadius to 25 for its pill-shaped buttons, and
// MUI treats a numeric sx borderRadius as a multiple of that — so `2` would
// render as 50px and turn every panel into a lozenge. Panels use explicit px.
const RADIUS = { panel: '12px', inset: '8px' };

// ---------- formatting ----------

/** Indian numbering: ₹1.2Cr / ₹39.4L / ₹4,500. Keeps dense cards readable. */
const fmtCurrency = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

const fmtCurrencyFull = (v) =>
  v === null || v === undefined || isNaN(v)
    ? '—'
    : `₹${Math.round(v).toLocaleString('en-IN')}`;

const fmtNumber = (v) =>
  v === null || v === undefined || isNaN(v) ? '—' : Math.round(v).toLocaleString('en-IN');

const fmtPct = (v, digits = 1) =>
  v === null || v === undefined || isNaN(v) ? '—' : `${v.toFixed(digits)}%`;

const fmtDays = (v) => (v === null || v === undefined || isNaN(v) ? '—' : `${v.toFixed(1)}d`);

// ---------- building blocks ----------

/**
 * One metric. `hero` doubles the type scale for the handful of numbers that
 * should dominate a section; everything else stays compact so a screen of
 * twenty KPIs still has a visual hierarchy.
 */
const Stat = ({ label, value, sub, tone = 'neutral', hero = false, title }) => {
  const theme = useTheme();
  const accent = TONE[tone] || TONE.neutral;

  const body = (
    <Card
      elevation={0}
      sx={{
        flex: hero ? '1 1 240px' : '1 1 150px',
        minWidth: hero ? 200 : 140,
        p: hero ? 2.25 : 1.75,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color .15s ease',
        '&:hover': { borderColor: alpha(accent, 0.6) },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: accent,
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: theme.palette.text.secondary,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: hero ? 30 : 21,
          fontWeight: 700,
          lineHeight: 1.15,
          wordBreak: 'break-word',
          color: theme.palette.text.primary,
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 0.4 }}>
          {sub}
        </Typography>
      )}
    </Card>
  );

  return title ? (
    <MuiTooltip title={title} arrow>
      {body}
    </MuiTooltip>
  ) : (
    body
  );
};

const Section = ({ title, caption, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 3.5 }}>
      {title && (
        <Box sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
            {title}
          </Typography>
          {caption && (
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 0.25 }}>
              {caption}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>{children}</Box>
    </Box>
  );
};

const EmptyState = ({ message = 'No data available', hint }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        color: theme.palette.text.secondary,
        textAlign: 'center',
        px: 2,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{message}</Typography>
      {hint && <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{hint}</Typography>}
    </Box>
  );
};

const ChartCard = ({ title, caption, children, height = 280, full = false, empty, emptyHint }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        flex: full ? '1 1 100%' : '1 1 440px',
        minWidth: 300,
        p: 2,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: caption ? 0.25 : 1.5 }}>{title}</Typography>
      {caption && (
        <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mb: 1.25 }}>
          {caption}
        </Typography>
      )}
      <Box sx={{ width: '100%', height }}>
        {empty ? <EmptyState hint={emptyHint} /> : children}
      </Box>
    </Card>
  );
};

const DataTable = ({ title, rows, columns, maxRows = 25, full = true }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        flex: full ? '1 1 100%' : '1 1 440px',
        minWidth: 300,
        p: 2,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>{title}</Typography>
      {!rows || !rows.length ? (
        <EmptyState message="Nothing to show" />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align || 'left'}
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: theme.palette.text.secondary,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, maxRows).map((r, i) => (
                <TableRow key={i} hover>
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align || 'left'}
                      sx={{ fontSize: 13, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}
                    >
                      {c.fmt ? c.fmt(r[c.key], r) : r[c.key] ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {rows.length > maxRows && (
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 1 }}>
              Showing {maxRows} of {rows.length} rows
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
};

// ---------- chart helpers ----------

const useAxis = () => {
  const theme = useTheme();
  return {
    tick: { fill: theme.palette.text.secondary, fontSize: 11 },
    stroke: alpha(theme.palette.text.secondary, 0.3),
    tickLine: false,
  };
};

const ChartTip = ({ formatter }) => {
  const theme = useTheme();
  return (
    <Tooltip
      formatter={formatter}
      contentStyle={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        fontSize: 12,
        color: theme.palette.text.primary,
      }}
      cursor={{ fill: alpha(theme.palette.text.secondary, 0.08) }}
    />
  );
};

/** Truncates long vendor names so they don't collide on a category axis. */
const shortLabel = (v) => {
  const s = String(v ?? '');
  return s.length > 18 ? `${s.slice(0, 17)}…` : s;
};

/** Horizontal bars read better than angled x-axis labels for named entities. */
const NamedBarChart = ({ data, dataKey = 'value', color, formatter, labelWidth = 150 }) => {
  const axis = useAxis();
  return (
    <ResponsiveContainer>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} horizontal={false} />
        <XAxis type="number" {...axis} tickFormatter={formatter} />
        <YAxis
          type="category"
          dataKey="name"
          width={labelWidth}
          {...axis}
          tickFormatter={shortLabel}
        />
        <ChartTip formatter={formatter} />
        <Bar dataKey={dataKey} fill={color || PALETTE[0]} radius={[0, 4, 4, 0]} maxBarSize={26} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const CountPieChart = ({ data, dataKey = 'count' }) => (
  <ResponsiveContainer>
    <PieChart>
      <Pie
        data={data}
        dataKey={dataKey}
        nameKey="name"
        innerRadius={55}
        outerRadius={95}
        paddingAngle={2}
      >
        {data.map((_, i) => (
          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
        ))}
      </Pie>
      <Legend wrapperStyle={{ fontSize: 12 }} />
      <ChartTip />
    </PieChart>
  </ResponsiveContainer>
);

// ---------- data-quality panel ----------

const SEVERITY_ICON = {
  warn: <WarningAmberIcon sx={{ fontSize: 18, color: TONE.warning }} />,
  info: <InfoOutlinedIcon sx={{ fontSize: 18, color: TONE.neutral }} />,
  ok: <CheckCircleOutlineIcon sx={{ fontSize: 18, color: TONE.positive }} />,
};

const DataQualityPanel = ({ items }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        flex: '1 1 100%',
        p: 2,
        borderRadius: RADIUS.panel,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>Data quality checks</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {items.map((item) => (
          <Box
            key={item.label}
            sx={{
              flex: '1 1 240px',
              minWidth: 220,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              p: 1.25,
              borderRadius: RADIUS.inset,
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
            }}
          >
            {SEVERITY_ICON[item.severity] || SEVERITY_ICON.info}
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{item.label}</Typography>
              <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                {item.detail}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

// ---------- tabs ----------

const OverviewTab = ({ a }) => {
  const k = a.overview.kpis;
  const c = a.overview.charts;
  const axis = useAxis();

  return (
    <>
      <Section
        title="Value across the procure-to-pay cycle"
        caption="Requisition → Purchase Order → Goods Receipt → Invoice → Payment"
      >
        <Stat hero label="Requisitioned" value={fmtCurrency(k.prValue)} title={fmtCurrencyFull(k.prValue)} />
        <Stat hero label="Ordered" value={fmtCurrency(k.poValue)} tone="neutral" title={fmtCurrencyFull(k.poValue)} />
        <Stat
          hero
          label="Received"
          value={fmtCurrency(k.receivedValue)}
          tone="positive"
          sub="derived from PO price"
          title={fmtCurrencyFull(k.receivedValue)}
        />
        <Stat hero label="Invoiced" value={fmtCurrency(k.invoicedValue)} tone="warning" title={fmtCurrencyFull(k.invoicedValue)} />
        <Stat
          hero
          label="Outstanding"
          value={fmtCurrency(k.outstanding)}
          tone="negative"
          title={fmtCurrencyFull(k.outstanding)}
        />
      </Section>

      <Section title="Flow efficiency">
        <Stat
          label="PR → PO value delta"
          value={fmtPct(k.prToPoValueDelta)}
          tone={k.prToPoValueDelta > 0 ? 'warning' : 'positive'}
          sub="ordered vs requisitioned"
        />
        <Stat label="PO → GR fulfilled" value={fmtPct(k.poToGrRate)} tone="positive" sub="by value" />
        <Stat label="GR → Invoiced" value={fmtPct(k.grToInvoiceRate)} tone="warning" sub="by value" />
        <Stat label="PR conversion" value={fmtPct(k.conversionRate)} sub="requisitions turned into POs" />
        <Stat
          label="On-time delivery"
          value={fmtPct(k.onTimeRate)}
          tone={k.onTimeRate >= 80 ? 'positive' : 'negative'}
        />
        <Stat label="Receipt coverage" value={fmtPct(k.receiptCoverage)} sub="PO lines fully received" />
      </Section>

      <Section title="Risk & exposure">
        <Stat
          label="Supplier concentration"
          value={fmtNumber(k.supplierHhi)}
          tone={k.supplierHhi > 2500 ? 'negative' : 'positive'}
          sub="HHI · >2500 is concentrated"
          title="Herfindahl-Hirschman Index across supplier spend (0–10000)"
        />
        <Stat
          label="Top supplier share"
          value={fmtPct(k.topSupplierShare)}
          tone={k.topSupplierShare > 50 ? 'negative' : 'neutral'}
          sub={k.topSupplierName}
        />
        <Stat label="Active suppliers" value={fmtNumber(k.activeSuppliers)} />
        <Stat label="Open requisitions" value={fmtNumber(k.openPrs)} tone="warning" />
        <Stat label="Lines pending invoice" value={fmtNumber(k.pendingInvoice)} tone="warning" />
        <Stat
          label="Overdue invoices"
          value={fmtNumber(k.overdueCount)}
          tone={k.overdueCount > 0 ? 'negative' : 'positive'}
        />
      </Section>

      <Section>
        <ChartCard title="Value at each stage" caption="Where value enters and leaves the cycle">
          <ResponsiveContainer>
            <BarChart data={c.valueFlow} margin={{ left: 4, right: 12, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="stage" {...axis} />
              <YAxis {...axis} tickFormatter={fmtCurrency} width={70} />
              <ChartTip formatter={fmtCurrencyFull} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={64}>
                {c.valueFlow.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Document volume" caption="Count of documents at each stage">
          <ResponsiveContainer>
            <BarChart data={c.funnel} layout="vertical" margin={{ left: 8, right: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} horizontal={false} />
              <XAxis type="number" {...axis} />
              <YAxis type="category" dataKey="stage" width={130} {...axis} />
              <ChartTip />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={30}>
                {c.funnel.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      <Section>
        <ChartCard
          title="Spend by supplier"
          caption="Top 10 by purchase order value"
          empty={!c.spendBySupplier.length}
        >
          <NamedBarChart data={c.spendBySupplier} color={PALETTE[0]} formatter={fmtCurrency} />
        </ChartCard>

        <ChartCard
          title="Delivery performance"
          caption="Goods receipt posting vs scheduled delivery date"
          empty={!c.deliveryBuckets.some((b) => b.count)}
        >
          <NamedBarChart
            data={c.deliveryBuckets}
            dataKey="count"
            color={PALETTE[2]}
            labelWidth={120}
          />
        </ChartCard>
      </Section>

      <Section>
        <DataQualityPanel items={a.overview.dataQuality} />
      </Section>
    </>
  );
};

const PoTab = ({ a }) => {
  const k = a.po.kpis;
  const c = a.po.charts;
  const axis = useAxis();

  return (
    <>
      <Section title="Purchase order volume & value">
        <Stat hero label="Total PO value" value={fmtCurrency(k.totalValue)} title={fmtCurrencyFull(k.totalValue)} />
        <Stat hero label="Purchase orders" value={fmtNumber(k.totalPos)} sub={`${fmtNumber(k.totalLines)} line items`} />
        <Stat label="Average PO value" value={fmtCurrency(k.avgPoValue)} title={fmtCurrencyFull(k.avgPoValue)} />
        <Stat label="Average line value" value={fmtCurrency(k.avgLineValue)} />
        <Stat
          label="Median line value"
          value={fmtCurrency(k.medianLineValue)}
          sub="half of lines are below this"
        />
        <Stat label="Lines per PO" value={k.linesPerPo ? k.linesPerPo.toFixed(2) : '—'} />
      </Section>

      <Section title="Supplier concentration">
        <Stat label="Active suppliers" value={fmtNumber(k.activeSuppliers)} />
        <Stat
          label="Top supplier"
          value={fmtPct(k.topSupplierShare)}
          sub={k.topSupplierName}
          tone={k.topSupplierShare > 50 ? 'negative' : 'neutral'}
        />
        <Stat
          label="Top 5 lines"
          value={fmtPct(k.top5LineShare)}
          sub="share of total spend"
          tone={k.top5LineShare > 80 ? 'warning' : 'neutral'}
        />
        <Stat
          label="Concentration (HHI)"
          value={fmtNumber(k.supplierHhi)}
          tone={k.supplierHhi > 2500 ? 'negative' : 'positive'}
          title="Herfindahl-Hirschman Index — above 2500 indicates a highly concentrated supplier base"
        />
      </Section>

      <Section title="Delivery & invoicing status">
        <Stat
          label="On-time delivery"
          value={fmtPct(k.onTimeRate)}
          tone={k.onTimeRate >= 80 ? 'positive' : 'negative'}
        />
        <Stat label="Average delay" value={fmtDays(k.avgDelayDays)} tone="warning" />
        <Stat label="Worst delay" value={k.worstDelayDays != null ? `${k.worstDelayDays}d` : '—'} tone="negative" />
        <Stat label="Fully received" value={fmtNumber(k.fullyReceived)} tone="positive" sub="PO lines" />
        <Stat label="Partially received" value={fmtNumber(k.partial)} tone="warning" sub="PO lines" />
        <Stat label="Not received" value={fmtNumber(k.notReceived)} tone="negative" sub="PO lines" />
        <Stat label="Pending invoice" value={fmtNumber(k.pendingInvoice)} tone="warning" sub="PO lines" />
      </Section>

      <Section>
        <ChartCard title="Spend by supplier" caption="Top 10 by value" empty={!c.spendBySupplier.length}>
          <NamedBarChart data={c.spendBySupplier} color={PALETTE[0]} formatter={fmtCurrency} />
        </ChartCard>

        <ChartCard
          title="Delivery timeliness"
          caption="How late goods arrived against the schedule line"
          empty={!c.deliveryBuckets.some((b) => b.count)}
        >
          <NamedBarChart data={c.deliveryBuckets} dataKey="count" color={PALETTE[2]} labelWidth={120} />
        </ChartCard>
      </Section>

      <Section>
        <ChartCard title="Value by plant" caption="Purchase order value per receiving plant" empty={!c.byPlant.length}>
          <ResponsiveContainer>
            <BarChart data={c.byPlant} margin={{ left: 4, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
              <XAxis dataKey="name" {...axis} />
              <YAxis {...axis} tickFormatter={fmtCurrency} width={70} />
              <ChartTip formatter={fmtCurrencyFull} />
              <Bar dataKey="value" fill={PALETTE[1]} radius={[4, 4, 0, 0]} maxBarSize={64} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Purchase order creation"
          caption={c.creationTrend.length < 2 ? 'Only one day of creation history exists in SAP' : 'Orders created per day'}
          empty={!c.creationTrend.length}
          emptyHint="No creation dates returned"
        >
          {c.creationTrend.length < 2 ? (
            <EmptyState
              message={`All ${fmtNumber(k.totalPos)} orders created on ${c.creationTrend[0]?.date || '—'}`}
              hint="A trend line needs more than one date — this is a limitation of the data, not the chart"
            />
          ) : (
            <ResponsiveContainer>
              <LineChart data={c.creationTrend} margin={{ left: 4, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={axis.stroke} vertical={false} />
                <XAxis dataKey="date" {...axis} />
                <YAxis {...axis} allowDecimals={false} width={40} />
                <ChartTip />
                <Line type="monotone" dataKey="count" stroke={PALETTE[0]} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </Section>

      <Section>
        <DataTable
          title="Three-way match status"
          rows={a.po.tables.matchStatus}
          columns={[
            { key: 'status', label: 'Status' },
            { key: 'lines', label: 'PO lines', align: 'right', fmt: fmtNumber },
          ]}
        />
      </Section>
    </>
  );
};

const PrTab = ({ a }) => {
  const k = a.pr.kpis;
  const c = a.pr.charts;

  return (
    <>
      <Section title="Requisition volume & value">
        <Stat hero label="Total PR value" value={fmtCurrency(k.totalValue)} title={fmtCurrencyFull(k.totalValue)} />
        <Stat hero label="Requisition lines" value={fmtNumber(k.totalPrs)} />
        <Stat label="Average PR value" value={fmtCurrency(k.avgPrValue)} />
        <Stat label="Released" value={fmtPct(k.releasedPct)} tone="positive" />
        <Stat label="Closed" value={fmtNumber(k.closedCount)} />
      </Section>

      <Section title="Conversion to purchase orders">
        <Stat
          label="Converted"
          value={fmtNumber(k.convertedCount)}
          tone="positive"
          sub="linked to a PO"
        />
        <Stat label="Still open" value={fmtNumber(k.openCount)} tone="warning" />
        <Stat label="Conversion rate" value={fmtPct(k.conversionRate)} sub="by line count" />
        <Stat label="Conversion by value" value={fmtPct(k.conversionValueRate)} />
        <Stat label="Average age (open)" value={fmtDays(k.avgAge)} />
        <Stat
          label="Oldest open"
          value={k.oldestOpen != null ? `${k.oldestOpen}d` : '—'}
          tone={k.oldestOpen > 30 ? 'negative' : 'neutral'}
        />
      </Section>

      <Section title="Requester concentration">
        <Stat label="Requesters" value={fmtNumber(k.requesterCount)} />
        <Stat
          label="Top requester"
          value={fmtPct(k.topRequesterShare)}
          sub={`${k.topRequester} · by value`}
          tone={k.topRequesterShare > 50 ? 'warning' : 'neutral'}
        />
        <Stat
          label="Concentration (HHI)"
          value={fmtNumber(k.requesterHhi)}
          tone={k.requesterHhi > 2500 ? 'negative' : 'positive'}
        />
      </Section>

      <Section>
        <ChartCard title="Requisition value by requester" empty={!c.byRequester.length}>
          <NamedBarChart data={c.byRequester} color={PALETTE[4]} formatter={fmtCurrency} labelWidth={110} />
        </ChartCard>

        <ChartCard title="Conversion status" caption="Requisitions that became purchase orders">
          <CountPieChart data={c.conversion} />
        </ChartCard>
      </Section>

      <Section>
        <ChartCard title="Top materials by value" empty={!c.byMaterial.length}>
          <NamedBarChart data={c.byMaterial} color={PALETTE[1]} formatter={fmtCurrency} labelWidth={170} />
        </ChartCard>

        <ChartCard
          title="Age of open requisitions"
          caption="Days since creation, for requisitions not yet ordered"
          empty={!c.agingBuckets.some((b) => b.count)}
        >
          <NamedBarChart data={c.agingBuckets} dataKey="count" color={PALETTE[2]} labelWidth={100} />
        </ChartCard>
      </Section>

      <Section>
        <DataTable
          title="Requisitions by requester"
          rows={c.byRequester}
          columns={[
            { key: 'name', label: 'Requester' },
            { key: 'count', label: 'Lines', align: 'right', fmt: fmtNumber },
            { key: 'value', label: 'Total value', align: 'right', fmt: fmtCurrencyFull },
          ]}
        />
      </Section>
    </>
  );
};

const GrTab = ({ a }) => {
  const k = a.gr.kpis;
  const c = a.gr.charts;

  return (
    <>
      <Section
        title="Goods receipt volume"
        caption="Receipt value is derived from the purchase order price — SAP returns zero in the movement amount field"
      >
        <Stat
          hero
          label="Received value"
          value={fmtCurrency(k.receivedValue)}
          tone="positive"
          title={fmtCurrencyFull(k.receivedValue)}
        />
        <Stat hero label="Goods receipts" value={fmtNumber(k.totalGrns)} sub={`${fmtNumber(k.totalLines)} lines`} />
        <Stat label="Quantity received" value={fmtNumber(k.totalQty)} />
        <Stat label="Avg qty per line" value={k.avgQtyPerLine ? k.avgQtyPerLine.toFixed(1) : '—'} />
        <Stat label="Most recent receipt" value={k.mostRecent} />
      </Section>

      <Section title="Coverage">
        <Stat label="Distinct POs receipted" value={fmtNumber(k.distinctPos)} />
        <Stat label="Distinct materials" value={fmtNumber(k.distinctMaterials)} />
        <Stat label="Plants" value={fmtNumber(k.distinctPlants)} />
        <Stat label="Suppliers" value={fmtNumber(k.supplierCount)} />
        <Stat
          label="Lines with PO link"
          value={fmtNumber(k.withPoLink)}
          tone={k.withPoLink === k.totalLines ? 'positive' : 'warning'}
        />
        <Stat
          label="Cancelled"
          value={fmtNumber(k.cancelled)}
          tone={k.cancelled > 0 ? 'negative' : 'positive'}
        />
        <Stat
          label="Could not be valued"
          value={fmtNumber(k.unpricedLines)}
          tone={k.unpricedLines > 0 ? 'warning' : 'positive'}
          sub="no matching PO price"
        />
      </Section>

      <Section>
        <ChartCard title="Receipt quantity by supplier" empty={!c.bySupplier.length}>
          <NamedBarChart data={c.bySupplier} color={PALETTE[1]} formatter={fmtNumber} />
        </ChartCard>

        <ChartCard title="Top materials received" caption="By quantity" empty={!c.byMaterial.length}>
          <NamedBarChart data={c.byMaterial} color={PALETTE[0]} formatter={fmtNumber} labelWidth={110} />
        </ChartCard>
      </Section>

      <Section>
        <ChartCard
          title="Movement types"
          caption={c.movementTypes.length === 1 ? `All receipts are type ${c.movementTypes[0]?.name}` : 'Distribution of goods movement types'}
          empty={!c.movementTypes.length}
        >
          {c.movementTypes.length === 1 ? (
            <EmptyState
              message={`Every receipt uses movement type ${c.movementTypes[0].name}`}
              hint="A breakdown needs more than one type"
            />
          ) : (
            <CountPieChart data={c.movementTypes} />
          )}
        </ChartCard>

        <ChartCard title="Receipts by plant" caption="Quantity received" empty={!c.byPlant.length}>
          <NamedBarChart data={c.byPlant} color={PALETTE[3]} formatter={fmtNumber} labelWidth={90} />
        </ChartCard>
      </Section>

      <Section>
        <DataTable
          title="Goods receipt detail"
          rows={a.gr.tables.recent}
          columns={[
            { key: 'document', label: 'Material doc' },
            { key: 'material', label: 'Material' },
            { key: 'supplier', label: 'Supplier' },
            { key: 'po', label: 'Purchase order' },
            { key: 'plant', label: 'Plant' },
            { key: 'qty', label: 'Quantity', align: 'right', fmt: fmtNumber },
          ]}
        />
      </Section>
    </>
  );
};

const ApTab = ({ a }) => {
  const k = a.ap.kpis;
  const c = a.ap.charts;

  const nothingPaid = k.paidTotal === 0;

  return (
    <>
      <Section
        title="Invoices & payables"
        caption="Totals count vendor line items only — goods receipt clearing entries are excluded"
      >
        <Stat hero label="Total invoiced" value={fmtCurrency(k.invoiceTotal)} title={fmtCurrencyFull(k.invoiceTotal)} />
        <Stat hero label="Outstanding" value={fmtCurrency(k.outstanding)} tone="negative" title={fmtCurrencyFull(k.outstanding)} />
        <Stat label="Invoices" value={fmtNumber(k.invoiceCount)} />
        <Stat label="Average invoice" value={fmtCurrency(k.avgInvoiceValue)} />
        <Stat label="Paid" value={fmtCurrency(k.paidTotal)} tone={nothingPaid ? 'warning' : 'positive'} />
        <Stat label="Payment ratio" value={fmtPct(k.paymentRatio)} tone={nothingPaid ? 'warning' : 'positive'} />
      </Section>

      <Section title="Ageing & risk">
        <Stat
          label="Overdue invoices"
          value={fmtNumber(k.overdueCount)}
          tone={k.overdueCount > 0 ? 'negative' : 'positive'}
        />
        <Stat label="Overdue value" value={fmtCurrency(k.overdueValue)} tone="negative" />
        <Stat label="Open line items" value={fmtNumber(k.openLineCount)} tone="warning" />
        <Stat label="Cleared line items" value={fmtNumber(k.clearedLineCount)} />
        <Stat label="Avg days to clear" value={fmtDays(k.avgDaysToClear)} />
        <Stat label="Vendors invoiced" value={fmtNumber(k.vendorCount)} />
        <Stat
          label="Vendor concentration"
          value={fmtNumber(k.vendorHhi)}
          tone={k.vendorHhi > 2500 ? 'negative' : 'positive'}
          sub={k.topVendor}
        />
        <Stat label="Credit memos" value={fmtNumber(k.creditMemos)} />
      </Section>

      <Section>
        <ChartCard
          title="Invoice ageing"
          caption="Against the due date calculated by SAP"
          empty={!c.agingBuckets.some((b) => b.count)}
        >
          <ResponsiveContainer>
            <BarChart data={c.agingBuckets} margin={{ left: 4, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha('#888', 0.3)} vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} tickFormatter={fmtCurrency} width={70} />
              <ChartTip formatter={fmtCurrencyFull} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={56}>
                {c.agingBuckets.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? PALETTE[1] : PALETTE[Math.min(i + 1, 3)]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Invoiced by vendor" caption="Top 10" empty={!c.byVendor.length}>
          <NamedBarChart data={c.byVendor} color={PALETTE[0]} formatter={fmtCurrency} />
        </ChartCard>
      </Section>

      <Section>
        <ChartCard
          title="Outstanding by vendor"
          caption="Uncleared vendor line items"
          empty={!c.outstandingByVendor.length}
          emptyHint="No open vendor items"
        >
          <NamedBarChart data={c.outstandingByVendor} color={PALETTE[3]} formatter={fmtCurrency} />
        </ChartCard>

        <ChartCard title="Payment activity" caption="Cleared vendor items over time">
          <EmptyState
            message={nothingPaid ? 'No payments recorded yet' : 'Payment history'}
            hint={
              nothingPaid
                ? 'Every vendor line item is still open — no clearing dates exist in SAP'
                : undefined
            }
          />
        </ChartCard>
      </Section>

      <Section>
        <DataTable
          title="Vendor payables summary"
          rows={a.ap.tables.vendorSummary}
          columns={[
            { key: 'vendor', label: 'Vendor' },
            { key: 'invoices', label: 'Invoices', align: 'right', fmt: fmtNumber },
            { key: 'invoiced', label: 'Invoiced', align: 'right', fmt: fmtCurrencyFull },
            { key: 'outstanding', label: 'Outstanding', align: 'right', fmt: fmtCurrencyFull },
          ]}
        />
      </Section>
    </>
  );
};

const TAB_VIEWS = { overview: OverviewTab, po: PoTab, pr: PrTab, gr: GrTab, ap: ApTab };

// ---------- main ----------

const PurchaseDashboard = ({ open, onClose }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [state, setState] = useState({ loading: false, error: null, raw: null, errors: {} });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data, errors } = await fetchPurchaseData();
      // Only a total wipeout is fatal. A service that 404s, or one that simply
      // has no records, must not blank the tabs that other services can fill —
      // the partial-failure banner reports what is missing instead.
      if (Object.keys(errors).length === Object.keys(data).length) {
        throw new Error(
          Object.entries(errors)
            .map(([key, message]) => `${key}: ${message}`)
            .join('\n') || 'No records returned.'
        );
      }
      setState({ loading: false, error: null, raw: data, errors });
    } catch (err) {
      setState({ loading: false, error: err.message, raw: null, errors: {} });
    }
  }, []);

  useEffect(() => {
    if (open) {
      setActiveTab('overview');
      load();
    }
  }, [open, load]);

  // Every tab is a view over one shared payload, so this runs once per load
  // rather than once per tab switch.
  const analytics = useMemo(
    () => (state.raw ? buildAllAnalytics(state.raw) : null),
    [state.raw]
  );

  const ActiveView = TAB_VIEWS[activeTab];
  const partialFailures = Object.keys(state.errors || {});

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 52, sm: 60 } }}>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
              Purchase Dashboard
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: theme.palette.text.secondary }}>
              Live from SAP OData
              {analytics && ` · ${fmtNumber(analytics.overview.counts.poItem)} PO lines · ${fmtNumber(analytics.overview.counts.prItem)} PR lines`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={load}
              disabled={state.loading}
              sx={{
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 999,
                px: 1.5,
              }}
            >
              Refresh
            </Button>
            <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderTop: `1px solid ${theme.palette.divider}`, minHeight: 44 }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={t.label}
              sx={{ fontWeight: 600, textTransform: 'none', fontSize: 13.5, minHeight: 44 }}
            />
          ))}
        </Tabs>
        {state.loading && <LinearProgress sx={{ height: 2 }} />}
      </AppBar>

      <Box sx={{ p: { xs: 1.5, sm: 2.5 }, overflowY: 'auto' }}>
        {state.loading && !analytics && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 12,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography sx={{ color: theme.palette.text.secondary }}>
              Loading purchase data from SAP…
            </Typography>
          </Box>
        )}

        {!state.loading && state.error && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 10,
              gap: 2,
            }}
          >
            <Chip label="Could not load dashboard" color="error" variant="outlined" />
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                textAlign: 'center',
                maxWidth: 560,
                fontSize: 13.5,
                whiteSpace: 'pre-line',
              }}
            >
              {state.error}
            </Typography>
            <Button variant="outlined" onClick={load}>
              Retry
            </Button>
          </Box>
        )}

        {analytics && !state.error && (
          <>
            {partialFailures.length > 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: RADIUS.panel,
                  border: `1px solid ${alpha(TONE.warning, 0.5)}`,
                  bgcolor: alpha(TONE.warning, 0.08),
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 18, color: TONE.warning, mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Some data could not be loaded
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                    {partialFailures.join(', ')} — figures below exclude these sources.
                  </Typography>
                  {partialFailures.map((key) => (
                    <Typography
                      key={key}
                      sx={{ fontSize: 11.5, color: theme.palette.text.secondary, opacity: 0.8, mt: 0.25 }}
                    >
                      {key}: {state.errors[key]}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            <ActiveView a={analytics} />
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default PurchaseDashboard;
